import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import * as Hooks from 'preact/hooks';
import { Fixtuur } from '@fixtuur/backpack';
import * as Initialisation from '../../store/initialisation';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selections from '../../store/selections';
import * as Selectors from '../../store/selectors';
import { useConfigurationFallbacks } from '../shared/useConfigurationFallbacks';
import { useGetCompatibleOptions } from '../shared/useGetOptions';
import { useCachingSelectionResolver } from './useCachingSelectionResolver';
import { Ui } from '@dora/vivian';

// TODO: Organise and clean up this entire file
export const useSelectHeroProp = (
    selectableBuildProperties: Ui.PropertyOption.PropertyChoice[],
    inspirationProductGroup: Fixtuur.InspirationProduct.InspirationProductGroup | null,
): {
    proposeHeroSelection: (propKey: string, propValue: string | number | null) => void;
    isHeroSelectionResolving: boolean;
} => {
    // STATE SELECTORS START
    const heroPropertySelections = useSelector(Selectors.Selections.buildPropertySelections);
    const inInspiration = useSelector(Selectors.Initialisation.inInspiration);
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);
    const productSelections = useSelector(Selectors.Selections.productSelections);
    const fixedProductSelections = useSelector(Selectors.Selections.groupingCodeSelections);
    const currentInspirationProduct = useSelector(
        Selectors.Initialisation.currentInspirationProductId,
    );

    // STATE SELECTORS END

    // HOOKS AND HELPERS START
    const { fallbacks, isFallbacksLoading } = useConfigurationFallbacks();
    const compatibleOptions = useGetCompatibleOptions();

    const resolveConfigurationSelection = useCachingSelectionResolver(
        serviceConfig,
        currentConfigurator?.configurationSpaceId,
        Fixtuur.Configuration.Selections.concatNullable(productSelections, heroPropertySelections),
        compatibleOptions,
        fixedProductSelections ?? Fixtuur.Configuration.Selections.empty(),
        fallbacks,
        isFallbacksLoading,
    );

    const dispatch = useDispatch();

    const [isHeroSelectionResolving, setIsHeroSelectionResolving] = Hooks.useState(false);

    const proposeHeroSelection = async (propKey: string, propValue: string | number | null) => {
        if (inInspiration) {
            pipe(
                O.Do,
                O.bind('productId', () => O.fromNullable(currentInspirationProduct)),
                O.bind('productGroup', () => O.fromNullable(inspirationProductGroup)),
                O.bind('originalSelections', ({ productGroup, productId }) =>
                    Fixtuur.InspirationProduct.propertySelections(productGroup, productId),
                ),
                O.bind('newSelections', () =>
                    pipe(
                        Fixtuur.Configuration.Selections.addValue(
                            Fixtuur.Configuration.Selections.empty(),
                            propKey,
                            propValue,
                        ),
                        O.fromNullable,
                    ),
                ),
                O.chain(({ productGroup, newSelections, originalSelections }) =>
                    Fixtuur.InspirationProduct.resolve(
                        [
                            newSelections,
                            // splitting here enables us to keep any compatible previous selections by the user,
                            // as Selections are atomicly handled in Inspiration Product resolution
                            ...Fixtuur.Configuration.Selections.split(
                                Fixtuur.Configuration.Selections.removeKeys(
                                    originalSelections,
                                    Object.keys(newSelections),
                                ),
                            ),
                        ],
                        productGroup,
                    ),
                ),
                O.map(newSelectedProductId => {
                    dispatch(
                        Initialisation.updateInspirationProduct({
                            productId: newSelectedProductId,
                        }),
                    );
                }),
            );
        } else {
            if (!currentConfigurator || !resolveConfigurationSelection || !compatibleOptions)
                return;

            setIsHeroSelectionResolving(true);

            resolveConfigurationSelection(propKey, propValue).then(async resolvedHeroSelections => {
                const productSelectionsByPart =
                    Fixtuur.Configuration.ProductSelectionsByPart.fromConfiguratorAndSelections(
                        currentConfigurator,
                        resolvedHeroSelections,
                    );

                dispatch(
                    Selections.setProductSelectionsByPart({
                        productSelectionsByPart,
                    }),
                );

                dispatch(
                    Selections.updateBuildPropertySelections({
                        newSelections: Fixtuur.Configuration.Selections.keepKeys(
                            resolvedHeroSelections,
                            selectableBuildProperties.map(sBP => sBP.name),
                        ),
                    }),
                );

                setIsHeroSelectionResolving(false);
            });
        }
    };
    // HOOKS AND HELPERS END

    return {
        proposeHeroSelection,
        isHeroSelectionResolving,
    };
};
